/* eslint-disable max-lines */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Edit01, Plus, SearchSm, SwitchVertical01 } from '@untitled-ui/icons-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../../components/buttons/button';
import { DropdownMenu, DropdownMenuItem } from '../../../components/dropdown-menu';
import { FeatureFlag } from '../../../components/feature-flag';
import { TextField } from '../../../components/inputs/text-field';
import { PageBlockerSubscription } from '../../../components/page-blocker';
import { PageBlockerContent } from '../../../components/page-blocker/page-blocker-content';
import { Pagination } from '../../../components/pagination';
import type { ArticleSortAndFilter } from '../../../entity/api/routes/article';
import { type User } from '../../../entity/api/user';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { getQueryClient } from '../../../sdk/request';
import {
  getArticleAnalysisFromCache,
  getArticleFunnelFromCache,
  useArticlesByBrandId,
  useAuthUser,
  useOrganizationBilling,
  useOrganizationUsersList,
  useUsersByIds,
} from '../../../services/storage';
import { useBrandsList } from '../../../services/storage/brands-queries';
import { useOrganizationsList } from '../../../services/storage/organizations-queries';
import { tNamespace } from '../../../services/translation';
import { isFirstTimeUser } from '../../../shared/billing';
import { getSelectedBrand } from '../../../shared/brand';
import { Feature } from '../../../shared/feature';
import { useDebounce } from '../../../shared/hooks/use-debounce';
import { isMobile } from '../../../shared/mobile';
import { getSelectedOrganization } from '../../../shared/organization';
import { isOrgCancelled } from '../../../shared/subscription';
import { layoutUserName } from '../../../shared/user';
import { useContentModals } from '../_content-modals-context';
import { ModalCancelledSub } from '../content/-editor/modal-cancelled-sub';
import { SectionContentList, SectionContentListState } from './-section-content-list';

const t = tNamespace('app.index');

const ARTICLES_PER_PAGE = 20;

// URL Search params type
type BlogsSearch = {
  page?: number;
  search?: string;
  createdBy?: string;
  sortBy?: 'createdAt' | 'updatedAt';
  createModal?: boolean;
  createKeyword?: string;
  rewriteModal?: boolean;
  rewriteUrl?: string;
};

export const Route = createFileRoute('/app/_sidebar-layout/blogs/')({
  validateSearch: function (search: Record<string, unknown>): BlogsSearch {
    // Validate page: must be a positive integer >= 1
    let validatedPage = 1;
    if (typeof search.page === 'number') {
      const pageNum = Math.floor(search.page);
      validatedPage = Math.max(pageNum, 1);
    }

    return {
      page: validatedPage,
      search: typeof search.search === 'string' ? search.search : undefined,
      createdBy: typeof search.createdBy === 'string' ? search.createdBy : undefined,
      sortBy:
        search.sortBy === 'createdAt' || search.sortBy === 'updatedAt' ? search.sortBy : undefined,
      ...(search.createModal === true || search.createModal === 'true'
        ? {
            createModal: true as const,
            createKeyword:
              typeof search.createKeyword === 'string' ? search.createKeyword : undefined,
          }
        : {}),
      ...(search.rewriteModal === true || search.rewriteModal === 'true'
        ? {
            rewriteModal: true as const,
            rewriteUrl: typeof search.rewriteUrl === 'string' ? search.rewriteUrl : undefined,
          }
        : {}),
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

type AuthorMap = Record<string, User>;

type AuthorItemsListProps = {
  authorList: User[];
  selectedAuthors: AuthorMap;
  onAuthorToggle: (author: User) => void;
};

function AuthorItemsList(props: AuthorItemsListProps) {
  const { authorList, selectedAuthors, onAuthorToggle } = props;

  return (
    <>
      {authorList.map(function (author) {
        return (
          <DropdownMenuItem
            key={author._id}
            onClick={function () {
              onAuthorToggle(author);
            }}
            label={
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedAuthors[author._id] != null}
                  onChange={function () {
                    onAuthorToggle(author);
                  }}
                />
                <span>{layoutUserName(author, true)}</span>
              </div>
            }
          />
        );
      })}
    </>
  );
}

function Index() {
  const navigate = useNavigate();
  const queryClient = getQueryClient();
  const search = Route.useSearch();
  const isMobileDevice = isMobile();
  const { openGenerationModal, openRewriteModal } = useContentModals();
  // URL state
  const currentPage = search.page ?? 1;
  const urlSearchTerm = search.search ?? '';
  const urlCreatedBy = search.createdBy ?? '';
  const urlSortBy = search.sortBy ?? 'createdAt';

  // Local state for immediate UI updates (before debounce/URL update)
  const [localSearchTerm, setLocalSearchTerm] = useState(urlSearchTerm);
  const [selectedFilter, setSelectedFilter] = useState({
    value: urlSortBy === 'updatedAt' ? 'updateDate' : 'creationDate',
    label: urlSortBy === 'updatedAt' ? t('updateDate') : t('creationDate'),
  });
  const [isOrganizationCanceled, setIsOrganizationCanceled] = useState(false);
  const [selectedAuthors, setSelectedAuthors] = useState<AuthorMap>({});
  const [hasRestoredAuthorsFromUrl, setHasRestoredAuthorsFromUrl] = useState(false);

  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  // Sync localSearchTerm with urlSearchTerm when URL changes externally (browser back/forward)
  useEffect(
    function () {
      setLocalSearchTerm(urlSearchTerm);
    },
    [urlSearchTerm]
  );

  // Sync selectedFilter with urlSortBy when URL changes externally (browser back/forward)
  useEffect(
    function () {
      setSelectedFilter({
        value: urlSortBy === 'updatedAt' ? 'updateDate' : 'creationDate',
        label: urlSortBy === 'updatedAt' ? t('updateDate') : t('creationDate'),
      });
    },
    [urlSortBy]
  );
  const filterItems = [
    { value: 'creationDate', label: t('creationDate') },
    { value: 'updateDate', label: t('updateDate') },
  ];

  // Get current user and brand info
  const { data: currentUser } = useAuthUser();
  const currentOrg = getSelectedOrganization(currentUser);
  const { data: orgBilling } = useOrganizationBilling(currentOrg?._id);
  const currentBrand = getSelectedBrand(currentUser);

  // Build query params for pagination
  const selectedAuthorIds = useMemo(
    function () {
      return Object.keys(selectedAuthors);
    },
    [selectedAuthors]
  );
  const queryParams: ArticleSortAndFilter = {
    offset: (currentPage - 1) * ARTICLES_PER_PAGE,
    limit: ARTICLES_PER_PAGE,
    search: debouncedSearchTerm.length > 0 ? debouncedSearchTerm : undefined,
    createdBy: selectedAuthorIds.length > 0 ? selectedAuthorIds : undefined,
    sortBy: selectedFilter.value === 'updateDate' ? 'updatedAt' : 'createdAt',
    sortByDir: 'DESC',
  };

  // TanStack Query hooks for articles data with pagination
  const articlesQuery = useArticlesByBrandId(currentBrand?._id, queryParams);
  const articleList = articlesQuery.data?.articles ?? [];
  const totalCount = articlesQuery.data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / ARTICLES_PER_PAGE);

  // Derive funnels and analysis from cache based on article list
  const articleFunnelList = useMemo(
    function () {
      return articleList
        .map(function (article) {
          return getArticleFunnelFromCache(queryClient, article._id);
        })
        .filter(function (funnel): funnel is NonNullable<typeof funnel> {
          return funnel != null;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleList]
  );

  const articleAnalysisList = useMemo(
    function () {
      return articleList
        .map(function (article) {
          return getArticleAnalysisFromCache(queryClient, article._id);
        })
        .filter(function (analysis): analysis is NonNullable<typeof analysis> {
          return analysis != null;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleList]
  );

  // TODO: missing reverse where needed
  // TODO: look into orderBy and setup index on updatedAt
  //       return storage.contents.reverse().sortBy('updatedAt');

  // TanStack Query for brands
  const { data: brandsList } = useBrandsList();

  // TanStack Query for org users by org ID
  const { data: orgUsersData } = useOrganizationUsersList(currentOrg?._id);
  const orgUsers = orgUsersData ?? [];
  const currentOrgUser = orgUsersData?.[0];

  // TODO: handle loading and error

  const organizationsQuery = useOrganizationsList();
  const organizations = { data: organizationsQuery.data ?? [] };

  const isCancelled = isOrgCancelled(orgBilling);

  // Extract user IDs from organization users to populate author filter
  const orgUserIds = useMemo(
    function () {
      return orgUsers
        .map(function (orgUser) {
          return orgUser.userId;
        })
        .filter(function (userId): userId is string {
          return userId != null && userId !== '';
        });
    },
    [orgUsers]
  );
  // Fetch users by IDs (they're likely already cached by useOrganizationUsersList)
  const { data: authorsData } = useUsersByIds(orgUserIds);
  const authorsList = authorsData ?? [];

  function getComponentState() {
    // Show loading state when fetching data
    if (
      (articlesQuery.isLoading || articlesQuery.isFetching) && // Only show loading skeleton if we don't have any data yet
      // This prevents showing skeleton when refetching in background
      articleList.length === 0
    ) {
      return SectionContentListState.Loading;
    }

    if (isFirstTimeUser(currentOrg) && totalCount === 0 && !articlesQuery.isLoading) {
      return SectionContentListState.FirstTimeCreating;
    }

    if (articleList.length === 0 && !articlesQuery.isLoading) {
      const isFiltering = debouncedSearchTerm.trim().length > 0 || selectedAuthorIds.length > 0;
      return isFiltering ? SectionContentListState.NoResult : SectionContentListState.Empty;
    }

    return SectionContentListState.NotEmpty;
  }

  const componentState = getComponentState();

  const canCreateContent = (function () {
    if (isCancelled) {
      return false;
    }

    const brand = brandsList?.[0];
    if (brand == null) {
      return false;
    }

    const { _id: brandId, organizationId } = brand;
    return (
      typeof brandId === 'string' &&
      brandId.length > 0 &&
      typeof organizationId === 'string' &&
      organizationId.length > 0
    );
  })();

  // Sync local search term with URL when debounced value changes
  useEffect(
    function () {
      if (debouncedSearchTerm !== urlSearchTerm) {
        navigate({
          from: Route.fullPath,
          search: function (prev) {
            return {
              ...prev,
              search: debouncedSearchTerm.length > 0 ? debouncedSearchTerm : undefined,
              page: 1, // Reset to page 1 on search
            };
          },
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.blogs/index.tsx',
              method: 'useEffect[debouncedSearchTerm]',
              infoMessage: 'Failed to update search in URL',
            },
            trackError
          );
        });
      }
    },
    [debouncedSearchTerm, urlSearchTerm, navigate]
  );

  // Sync selected authors with URL (only after initial restoration from URL)
  useEffect(
    function () {
      // Don't sync to URL until we've had a chance to restore from URL
      if (!hasRestoredAuthorsFromUrl) {
        return;
      }

      const authorIdsString = selectedAuthorIds.join(',');
      if (authorIdsString !== urlCreatedBy) {
        navigate({
          from: Route.fullPath,
          search: function (prev) {
            return {
              ...prev,
              createdBy: selectedAuthorIds.length > 0 ? authorIdsString : undefined,
              page: 1, // Reset to page 1 on filter change
            };
          },
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.blogs/index.tsx',
              method: 'useEffect[selectedAuthorIds]',
              infoMessage: 'Failed to update createdBy in URL',
            },
            trackError
          );
        });
      }
    },
    [selectedAuthorIds, urlCreatedBy, navigate, hasRestoredAuthorsFromUrl]
  );

  // Parse URL createdBy into selectedAuthors map
  useEffect(
    function () {
      if (urlCreatedBy.length > 0 && authorsList.length > 0) {
        const authorIds = urlCreatedBy.split(',').filter(Boolean);
        const authorsMap: AuthorMap = {};
        for (const authorId of authorIds) {
          const author = authorsList.find(function (a) {
            return a._id === authorId;
          });
          if (author != null) {
            authorsMap[authorId] = author;
          }
        }
        setSelectedAuthors(authorsMap);
        setHasRestoredAuthorsFromUrl(true);
      } else if (urlCreatedBy.length === 0) {
        // No URL param to restore, mark as restored immediately
        setHasRestoredAuthorsFromUrl(true);
      }
    },
    [urlCreatedBy, authorsList]
  );

  // Redirect to valid page if current page is out of bounds
  useEffect(
    function () {
      if (!articlesQuery.isLoading && totalPages > 0) {
        // Redirect if page is too high or too low
        if (currentPage > totalPages) {
          navigate({
            from: Route.fullPath,
            search: function (prev) {
              return {
                ...prev,
                page: totalPages,
              };
            },
            replace: true,
          }).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/_sidebar-layout.blogs/index.tsx',
                method: 'useEffect[pageValidation]',
                infoMessage: 'Failed to redirect to valid page',
              },
              trackError
            );
          });
        } else if (currentPage < 1) {
          navigate({
            from: Route.fullPath,
            search: function (prev) {
              return {
                ...prev,
                page: 1,
              };
            },
            replace: true,
          }).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/_sidebar-layout.blogs/index.tsx',
                method: 'useEffect[pageValidation]',
                infoMessage: 'Failed to redirect to valid page',
              },
              trackError
            );
          });
        }
      }
    },
    [currentPage, totalPages, articlesQuery.isLoading, navigate]
  );

  function handleSortChange(value: string, label: string) {
    setSelectedFilter({ value, label });
    const sortBy = value === 'updateDate' ? 'updatedAt' : 'createdAt';
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          sortBy,
          page: 1, // Reset to page 1 on sort change
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.blogs/index.tsx',
          method: 'handleSortChange',
          infoMessage: 'Failed to update sortBy in URL',
        },
        trackError
      );
    });
  }

  function handlePageChange(page: number) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return { ...prev, page };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.blogs/index.tsx',
          method: 'handlePageChange',
          infoMessage: 'Failed to navigate to page',
        },
        trackError
      );
    });
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setLocalSearchTerm(e.target.value);
  }

  function handleAuthorToggle(author: User) {
    const isSelected = selectedAuthors[author._id] != null;
    if (!isSelected) {
      setSelectedAuthors({ ...selectedAuthors, [author._id]: author });
    } else {
      const newSelectedAuthors = { ...selectedAuthors };
      delete newSelectedAuthors[author._id];
      setSelectedAuthors(newSelectedAuthors);
    }
  }

  function handleClearFilters() {
    setLocalSearchTerm('');
    setSelectedAuthors({});
    setSelectedFilter({
      value: 'creationDate',
      label: t('creationDate'),
    });
    navigate({
      from: Route.fullPath,
      search: {
        page: 1,
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.blogs/index.tsx',
          method: 'handleClearFilters',
          infoMessage: 'Failed to clear filters',
        },
        trackError
      );
    });
  }

  const hasHandledCreateModalRef = useRef(false);
  const hasHandledRewriteModalRef = useRef(false);

  // Open the creation modal when navigated here with createModal=true (e.g. from SEO keywords)
  useEffect(
    function () {
      if (
        !search.createModal ||
        hasHandledCreateModalRef.current ||
        brandsList == null ||
        currentUser == null
      ) {
        return;
      }
      hasHandledCreateModalRef.current = true;

      // Strip the params from the URL so a refresh doesn't re-open the modal
      navigate({
        from: Route.fullPath,
        search: function (prev) {
          const next = { ...prev };
          delete next.createModal;
          delete next.createKeyword;
          return next;
        },
        replace: true,
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.blogs/index.tsx',
            method: 'useEffect[createModal]',
            infoMessage: 'Failed to strip createModal from URL',
          },
          trackError
        );
      });

      openGenerationModal({ keyword: search.createKeyword });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search.createModal, brandsList, currentUser]
  );

  // Open the rewrite modal when navigated here with rewriteModal=true (e.g. from SEO keywords refresh)
  useEffect(
    function () {
      if (
        !search.rewriteModal ||
        hasHandledRewriteModalRef.current ||
        brandsList == null ||
        currentUser == null
      ) {
        return;
      }
      hasHandledRewriteModalRef.current = true;

      navigate({
        from: Route.fullPath,
        search: function (prev) {
          const next = { ...prev };
          delete next.rewriteModal;
          delete next.rewriteUrl;
          return next;
        },
        replace: true,
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.blogs/index.tsx',
            method: 'useEffect[rewriteModal]',
            infoMessage: 'Failed to strip rewriteModal from URL',
          },
          trackError
        );
      });

      openRewriteModal({ url: search.rewriteUrl });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search.rewriteModal, brandsList, currentUser]
  );

  // When brand changes, refetch articles using TanStack Query's built-in refetch
  // The useArticlesByBrandId hook automatically refetches when brandId changes
  // No manual useEffect needed - TanStack Query handles this

  return (
    <div className="relative h-full w-full overflow-x-hidden">
      <div className="relative h-full flex-1 overflow-x-hidden overflow-y-scroll p-4 md:px-0 md:pb-12 md:pt-12">
        <div className="mx-auto flex h-full w-full min-w-0 max-w-full flex-col md:max-w-content">
          <div className="mb-8 flex items-center justify-between md:mb-16">
            <p className="font-display-2 hidden md:block">{t('title')}</p>
            {componentState === SectionContentListState.Empty ||
            componentState === SectionContentListState.FirstTimeCreating ? null : (
              <div className="flex gap-5">
                <FeatureFlag flags={Feature.REWRITE_CONTENT}>
                  <Button
                    variant="outline"
                    size="md"
                    leftIcon={<Edit01 width={16} height={16} />}
                    onClick={function () {
                      openRewriteModal();
                    }}
                    disabled={!canCreateContent || isMobileDevice}
                  >
                    {t('rewriteContent')}
                  </Button>
                </FeatureFlag>
                <Button
                  size="md"
                  variant="accent"
                  onClick={function () {
                    openGenerationModal();
                  }}
                  className="flex items-center gap-2 text-sm/5 font-semibold tracking-normal"
                  disabled={!canCreateContent}
                  leftIcon={<Plus width={16} height={16} />}
                >
                  {t('newContent')}
                </Button>
              </div>
            )}
          </div>
          {componentState === SectionContentListState.Empty ||
          componentState === SectionContentListState.FirstTimeCreating ? null : (
            <div className="mb-10 flex items-center justify-between">
              <DropdownMenu
                disabled={articleList.length === 0}
                position="start"
                closeOnSelect
                trigger={
                  <div className="font-body-2-sb inline-flex h-9 items-center justify-center gap-2 text-nowrap rounded-md border border-branding-gray bg-white px-3.5 py-2 focus:outline-none data-[state=open]:bg-gray-100">
                    <span className="hidden md:block">{selectedFilter.label}</span>
                    <SwitchVertical01 className="text-black" width={14} height={14} />
                  </div>
                }
                contentClassName="min-w-[200px]"
              >
                <div className="flex flex-col gap-1">
                  {filterItems.map(function (item) {
                    return (
                      <DropdownMenuItem
                        key={item.value}
                        onClick={function () {
                          handleSortChange(item.value, item.label);
                        }}
                        label={
                          <div className="flex items-center gap-2">
                            <span>{item.label}</span>
                          </div>
                        }
                      />
                    );
                  })}
                </div>
              </DropdownMenu>
              <div className="flex items-center gap-2.5">
                <DropdownMenu
                  disabled={authorsList.length === 0}
                  position="start"
                  trigger={
                    <div className="font-body-2-sb inline-flex h-9 items-center justify-center gap-2 text-nowrap rounded-md border border-branding-gray bg-white px-3.5 py-2 focus:outline-none data-[state=open]:bg-gray-100">
                      <span className="hidden md:block">{t('createdBy')}</span>
                      {selectedAuthorIds.length > 0 ? (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-branding-accent text-xs text-white">
                          {selectedAuthorIds.length}
                        </span>
                      ) : (
                        <ChevronDown width={16} height={16} className="text-black" />
                      )}
                    </div>
                  }
                  contentClassName="min-w-[200px]"
                >
                  <div className="flex flex-col gap-2">
                    <AuthorItemsList
                      authorList={authorsList}
                      selectedAuthors={selectedAuthors}
                      onAuthorToggle={handleAuthorToggle}
                    />
                  </div>
                </DropdownMenu>
                <TextField
                  sizeField="md"
                  iconLeft={<SearchSm width={16} height={16} />}
                  placeholder={t('searchPlaceholder')}
                  value={localSearchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          )}
          <SectionContentList
            articles={articleList}
            contentFunnelList={articleFunnelList ?? []}
            contentAnalysisList={articleAnalysisList ?? []}
            authorList={authorsList}
            selectedAuthors={selectedAuthors}
            onClearFilters={handleClearFilters}
            onCreateContent={function () {
              openGenerationModal();
            }}
            onCreateRewriteContent={function () {
              openRewriteModal();
            }}
            canCreateContent={canCreateContent}
            currentOrganization={currentOrg}
            componentState={componentState}
            skeletonCount={ARTICLES_PER_PAGE}
          />

          {/* Pagination */}
          {componentState === SectionContentListState.NotEmpty && totalPages > 1 ? (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="py-8"
            />
          ) : null}
        </div>

        <ModalCancelledSub
          isOpen={isOrganizationCanceled}
          onOpenChange={setIsOrganizationCanceled}
          backButton={function () {
            setIsOrganizationCanceled(false);
          }}
          reactivateSubscription={function () {
            // TODO: we should get organization from state defining the selected organization
            const organization = (organizations?.data ?? []).find(
              (org) => org?._id === brandsList?.[0]?.organizationId
            );
            if (organization == null) {
              return;
            }
            const link = `/app/settings/organization/${organization._id}/plan`;
            void navigate({ to: link });
          }}
        />
      </div>
      <PageBlockerContent organizationBilling={orgBilling} />
      <PageBlockerSubscription organizationUser={currentOrgUser} organizationBilling={orgBilling} />
    </div>
  );
}
